// src/components/AssistantServicesModal.tsx
// Panel para que la secretaria edite el PRECIO de los servicios de un profesional.
// Solo precios (no crea/borra servicios ni toca horarios). Requiere permiso del backend.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, DollarSign, Check, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface ProfessionalOption {
  professional_id: number;
  nombre: string;
  apellido: string;
  especialidad?: string;
}

interface ServiceRow {
  id: number;
  name: string;
  duration: number;
  price: number | string;
  price_mode?: 'fijo' | 'obra_social' | 'consultar';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  professionals: ProfessionalOption[];
  preselectedProfId?: number | null;
}

const AssistantServicesModal: React.FC<Props> = ({ isOpen, onClose, professionals, preselectedProfId }) => {
  const [profId, setProfId] = useState<number | null>(null);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [prices, setPrices] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [error, setError] = useState('');

  // Elegir profesional inicial al abrir
  useEffect(() => {
    if (!isOpen) return;
    const initial = preselectedProfId || professionals[0]?.professional_id || null;
    setProfId(initial);
  }, [isOpen, preselectedProfId, professionals]);

  // Cargar servicios del profesional elegido
  useEffect(() => {
    if (!isOpen || !profId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/assistants/professional/${profId}/services`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.success && Array.isArray(data.services)) {
          setServices(data.services);
          const initialPrices: Record<number, string> = {};
          data.services.forEach((s: ServiceRow) => { initialPrices[s.id] = String(parseFloat(String(s.price)) || 0); });
          setPrices(initialPrices);
        } else {
          setServices([]);
          setError(data.error || 'No se pudieron cargar los servicios');
        }
      } catch {
        if (!cancelled) setError('Error de conexión');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, profId]);

  const savePrice = async (serviceId: number) => {
    if (!profId) return;
    const newPrice = Number(prices[serviceId]);
    if (!Number.isFinite(newPrice) || newPrice < 0) {
      setError('Ingresá un precio válido');
      return;
    }
    try {
      setSavingId(serviceId);
      setError('');
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/assistants/professional/${profId}/services/${serviceId}/price`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ price: newPrice })
        }
      );
      const data = await res.json();
      if (data.success) {
        setServices(prev => prev.map(s => (s.id === serviceId ? { ...s, price: newPrice } : s)));
        setSavedId(serviceId);
        setTimeout(() => setSavedId(prev => (prev === serviceId ? null : prev)), 1800);
      } else {
        setError(data.error || 'No se pudo guardar el precio');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setSavingId(null);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-amber-500">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-white" />
            <h2 className="text-lg font-bold text-white">Precios de servicios</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {/* Selector de profesional (si hay más de uno) */}
          {professionals.length > 1 && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Profesional</label>
              <select
                value={profId ?? ''}
                onChange={(e) => setProfId(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
              >
                {professionals.map(p => (
                  <option key={p.professional_id} value={p.professional_id}>
                    {p.nombre} {p.apellido}{p.especialidad ? ` — ${p.especialidad}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Cargando servicios…</div>
          ) : services.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">Este profesional no tiene servicios activos.</div>
          ) : (
            <div className="space-y-2">
              {services.map(s => {
                const changed = String(Number(prices[s.id])) !== String(parseFloat(String(s.price)) || 0);
                return (
                  <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/60">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 text-sm truncate">{s.name}</p>
                      <p className="text-xs text-gray-400">{s.duration} min</p>
                    </div>
                    {(s.price_mode ?? 'fijo') !== 'fijo' ? (
                      // Este servicio no cobra por precio: mostrar la etiqueta en vez
                      // de un importe editable que no tendria efecto.
                      <span className="px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 text-xs font-medium whitespace-nowrap">
                        {s.price_mode === 'obra_social' ? 'Obra social' : 'A consultar'}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          value={prices[s.id] ?? ''}
                          onChange={(e) => setPrices(prev => ({ ...prev, [s.id]: e.target.value }))}
                          className="w-28 px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => savePrice(s.id)}
                      disabled={savingId === s.id || !changed || (s.price_mode ?? 'fijo') !== 'fijo'}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                        savedId === s.id
                          ? 'bg-green-100 text-green-700'
                          : changed
                            ? 'bg-orange-500 text-white hover:bg-orange-600'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {savingId === s.id ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : savedId === s.id ? (
                        <><Check className="w-4 h-4" /> Guardado</>
                      ) : (
                        'Guardar'
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssistantServicesModal;
